// Thin wrappers over the Web Speech API (window.speechSynthesis +
// SpeechRecognition). Returns no-op stubs when the browser doesn't
// support them (Safari < 14 etc), so callers can render the trigger
// button optimistically and degrade quietly.

export function speakText(text: string, opts: { rate?: number; lang?: string } = {}): () => void {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return () => undefined;
  const synth = window.speechSynthesis;
  synth.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  utter.rate = opts.rate ?? 1.0;
  utter.lang = opts.lang ?? "en-US";
  synth.speak(utter);
  return () => synth.cancel();
}

export function isSpeechSynthesisAvailable(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

export function isSpeechRecognitionAvailable(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
}

export interface RecognitionHandle {
  stop(): void;
}

export function startSpeechRecognition(
  onResult: (text: string) => void,
  opts: { lang?: string; interim?: boolean } = {},
): RecognitionHandle | null {
  if (!isSpeechRecognitionAvailable()) return null;
  const Ctor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  const rec = new Ctor();
  rec.lang = opts.lang ?? "en-US";
  rec.interimResults = !!opts.interim;
  rec.continuous = false;
  rec.maxAlternatives = 1;
  rec.onresult = (e: any) => {
    const text = e?.results?.[0]?.[0]?.transcript;
    if (typeof text === "string" && text.length > 0) onResult(text);
  };
  try {
    rec.start();
  } catch {
    return null;
  }
  return { stop: () => { try { rec.stop(); } catch { /* ignore */ } } };
}
